import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-bootstrap';
import MessageModal from '@/components/ui/MessageModal';
import LoadingButton from '@/components/ui/LoadingButton';
import StatusModal from '@/components/ui/StatusModal';
import IdolSelector from '@/components/ui/IdolSelector';
import Axios from '@/shared/api/axios';

// 表單初始值：第一次進頁面、或偶像尚未設定過聊天設定（API 成功但沒有 data）時顯示
const INITIAL_SETTINGS = {
  chatEnabled: true,
  onlySubscribers: false,
  tippingEnabled: true,
  welcomeMessage: '',
};

function ChatSettings() {
  const { t } = useTranslation();
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedIdol, setSelectedIdol] = useState('');
  const [currentStatusCode, setCurrentStatusCode] = useState('');
  const successModalRef = useRef();
  const statusModalRef = useRef();

  // 表單目前載入的是哪個偶像的設定。切換偶像後尚未載入完成、或載入失敗時會與 selectedIdol 不一致，
  // 此時表單仍是前一個偶像的值，停用儲存，避免把它存進新選的偶像
  const [loadedIdolId, setLoadedIdolId] = useState(null);
  // 防亂序：快速切換偶像時，只套用最後一次請求的結果
  const fetchSeqRef = useRef(0);

  // 聊天設定狀態
  const [chatEnabled, setChatEnabled] = useState(INITIAL_SETTINGS.chatEnabled);
  const [onlySubscribers, setOnlySubscribers] = useState(INITIAL_SETTINGS.onlySubscribers);
  const [tippingEnabled, setTippingEnabled] = useState(INITIAL_SETTINGS.tippingEnabled);
  const [welcomeMessage, setWelcomeMessage] = useState(INITIAL_SETTINGS.welcomeMessage);

  // 處理聊天功能開關
  const handleChatEnabledChange = (checked) => {
    setChatEnabled(checked);
    setHasChanges(true);
  };

  // 處理僅訂閱者開關
  const handleOnlySubscribersChange = (checked) => {
    setOnlySubscribers(checked);
    setHasChanges(true);
  };

  // 處理打賞開關
  const handleTippingEnabledChange = (checked) => {
    setTippingEnabled(checked);
    setHasChanges(true);
  };

  // 處理歡迎訊息變更
  const handleWelcomeMessageChange = (e) => {
    setWelcomeMessage(e.target.value);
    setHasChanges(true);
  };

  // 當選擇的偶像改變時，載入設定
  useEffect(() => {
    if (selectedIdol) {
      fetchChatSettings();
    }
  }, [selectedIdol]);

  // 取得聊天設定 (5-4)
  const fetchChatSettings = async () => {
    if (!selectedIdol) return;

    const requestIdol = selectedIdol;
    const seq = ++fetchSeqRef.current;
    try {
      const response = await Axios.post('/chat/get-setting', {
        idol_id: requestIdol
      });
      if (seq !== fetchSeqRef.current) return;

      if (response.data?.status_code === 'SYSTEM_1000') {
        const data = response.data.data;
        if (data) {
          setChatEnabled(data.chat_enabled);
          setOnlySubscribers(data.subscribers_only);
          setTippingEnabled(data.tip_enabled);
          setWelcomeMessage(data.welcome_message || '');
        } else {
          // 尚未設定過聊天設定：顯示初始值，不沿用前一個偶像留在表單上的值
          setChatEnabled(INITIAL_SETTINGS.chatEnabled);
          setOnlySubscribers(INITIAL_SETTINGS.onlySubscribers);
          setTippingEnabled(INITIAL_SETTINGS.tippingEnabled);
          setWelcomeMessage(INITIAL_SETTINGS.welcomeMessage);
        }
        setHasChanges(false);
        setLoadedIdolId(requestIdol);
      } else if (response.data?.status_code) {
        setCurrentStatusCode(response.data.status_code);
        statusModalRef.current?.show();
      }
    } catch (error) {
      if (seq !== fetchSeqRef.current) return;
      // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        setCurrentStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      } else if (!error.httpErrorCode) {
        console.error('Failed to fetch chat settings:', error);
      }
    }
  };

  // 儲存設定 (5-5)
  const handleSave = async () => {
    // 儲存鍵已在表單不是目前偶像的設定時停用，這裡再擋一次
    if (!selectedIdol || loadedIdolId !== selectedIdol) return;
    // 儲存途中若切換偶像，effect 已送出新偶像的讀取（序號前進）；回來後不可再動表單或重抓舊偶像，
    // 否則舊偶像的重抓會作廢新偶像的讀取，表單停在舊偶像、儲存鍵一直停用
    const seqAtSave = fetchSeqRef.current;

    setIsSaving(true);
    try {
      const response = await Axios.post('/chat/update-setting', {
        idol_id: selectedIdol,
        chat_enabled: chatEnabled,
        subscribers_only: onlySubscribers,
        tip_enabled: tippingEnabled,
        welcome_message: welcomeMessage
      });

      if (response.data?.status_code === 'SYSTEM_1000') {
        successModalRef.current?.show();
        if (seqAtSave !== fetchSeqRef.current) return;
        setHasChanges(false);
        // 重新載入設定
        fetchChatSettings();
      } else if (response.data?.status_code) {
        setCurrentStatusCode(response.data.status_code);
        statusModalRef.current?.show();
      }
    } catch (error) {
      console.error('Failed to save chat settings:', error);
      // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        setCurrentStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      <h3 className="mb-4">{t('chat_settings')}</h3>

      {/* 偶像選擇器 */}
      <IdolSelector
        value={selectedIdol}
        onChange={setSelectedIdol}
        className="mb-4"
      />

      <div className="bg-white rounded p-4 mb-4">
        {/* 聊天私訊功能 */}
        <div className="mb-4">
          <div className="d-flex align-items-center gap-3 mb-3">
            <h5 className="mb-0">{t('chat_private_message')}</h5>
            <Form.Check
              type="switch"
              id="chat-enabled-switch"
              checked={chatEnabled}
              onChange={(e) => handleChatEnabledChange(e.target.checked)}
              className="fs-5"
            />
          </div>

          {/* 僅訂閱者可發送訊息 */}
          <div className="mb-2">
            <Form.Check
              type="checkbox"
              id="only-subscribers-check"
              label={t('only_subscribers_can_message')}
              checked={onlySubscribers}
              onChange={(e) => handleOnlySubscribersChange(e.target.checked)}
              disabled={!chatEnabled}
            />
          </div>

          {/* 開啟打賞 */}
          <div className="mb-3">
            <Form.Check
              type="checkbox"
              id="tipping-enabled-check"
              label={t('enable_tipping')}
              checked={tippingEnabled}
              onChange={(e) => handleTippingEnabledChange(e.target.checked)}
              disabled={!chatEnabled}
            />
          </div>
        </div>

        {/* 歡迎訊息 */}
        <div className="mb-4">
          <Form.Group>
            <Form.Label className="h5 mb-3">{t('welcome_message')}</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={welcomeMessage}
              onChange={handleWelcomeMessageChange}
              placeholder={t('welcome_message_placeholder')}
              className="user-textarea"
            />
          </Form.Group>
        </div>

        {/* 儲存按鈕 */}
        <div className="d-flex justify-content-end">
          <LoadingButton
            className="btn btn-primary rounded-pill px-4"
            onClick={handleSave}
            disabled={!hasChanges || loadedIdolId !== selectedIdol}
            isLoading={isSaving}
          >
            {t('save')}
          </LoadingButton>
        </div>
      </div>

      {/* 成功訊息 Modal */}
      <MessageModal ref={successModalRef} status="success">
        {t('saved_successfully')}
      </MessageModal>

      {/* Status Modal */}
      <StatusModal ref={statusModalRef} statusCode={currentStatusCode} />
    </div>
  );
}

export default ChatSettings;
